/**
 * The single HTTP client for the skill-service surfaces. Every call attaches the
 * bearer session, and every error is surfaced *honestly* — the server's documented
 * `{ error, reason }` shapes (401 invalid_session, 403 insufficient_role /
 * cannot_assign_role, 409 last_owner / locked_by_other, 4xx state conflicts, 5xx)
 * are mapped to a readable message rather than swallowed (thin-client contract §9).
 *
 * On a 401 the session is cleared and an `onUnauthorized` hook fires so the app
 * drops back to the sign-in shell instead of showing a broken authed screen.
 */

import { clearToken, getToken } from "../auth/session";
import type {
  AccountContext,
  AuthConfig,
  EmailVerifyResponse,
  EntitlementGate,
  HandoffDetail,
  HandoffFilters,
  HandoffGap,
  HandoffListResponse,
  HandoffSummary,
  LocksResponse,
  CreateInviteResponse,
  InvitesResponse,
  MembersResponse,
  PendingAssignment,
  PendingResponse,
  ReleaseInfo,
  Role,
  TriageStatus,
  TrashResponse,
} from "./types";

// Same-origin by default (SPA served behind the same host that proxies the API);
// override at build time with VITE_API_BASE for a split-origin deploy.
const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly reason: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Map a server error body to a human message. Falls back to the raw reason. */
function messageFor(status: number, reason: string | undefined, error: string | undefined): string {
  switch (reason) {
    case "no_account_context":
      return "Your session has no account context. Sign out and sign in again.";
    case "insufficient_role":
      return "You don't have permission to do that.";
    case "cannot_assign_role":
      return "Your role can't grant that role. Owners are owner-assigned only.";
    case "last_owner":
      return "An account must keep at least one owner — you can't remove the last one.";
    case "unknown_member":
      return "That user is no longer part of this account.";
    case "cross_domain":
      return "You can only pre-assign people at your own firm's email domain for now.";
    case "already_member":
      return "That person has already joined — change their role from the members list instead.";
    case "unknown_pending":
      return "That invite no longer exists — it may have been used or already revoked.";
    case "unknown_invite":
      return "That invite link no longer exists — it may have been used or revoked.";
    case "invalid_invite":
      return "This invite link is invalid or has already been used. Ask for a fresh one.";
    case "invite_expired":
      return "This invite link has expired. Ask your workspace admin for a new one.";
    case "locked_by_other":
      return "This skill is locked by another user. Force-release the lock first.";
    case "not_trashed":
      return "That skill isn't in the trash.";
    case "already_trashed":
      return "That skill is already in the trash.";
    case "already_deleted":
      return "That skill has already been permanently deleted.";
    case "not_locked":
      return "That skill isn't checked out.";
    case "trashed":
      return "That skill is in the trash — restore it first.";
    case "not_entitled":
      return "Your account isn't active yet. Once your subscription is enabled you'll be able to download the installer.";
    case "no_release":
      return "No installer has been published yet. Please check back shortly.";
    default:
      break;
  }
  if (error === "invalid_email") return "Enter a valid email address.";
  if (error === "invalid_role" || error === "invalid_body") return "That request was malformed.";
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status >= 500) return "The server hit an error. Please try again in a moment.";
  return error ?? reason ?? `Request failed (${status}).`;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  auth = true,
): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  if (auth) {
    const token = getToken();
    if (token) headers["authorization"] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      // Send cookies cross-origin. The API (skill.treillage.ai) sits behind
      // Cloudflare Access; the CF_Authorization cookie set when sign-in navigates
      // through Access must ride along on these data fetches or Access 302s them.
      // Requires the server's Access-Control-Allow-Credentials: true (TRE-783 CORS).
      credentials: "include",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // Transport failure (offline, DNS, CORS). Fail honestly, don't pretend success.
    throw new ApiError(0, "network", "Couldn't reach the server. Check your connection.");
  }

  if (res.status === 401) {
    clearToken();
    onUnauthorized?.();
    throw new ApiError(401, "invalid_session", "Your session has expired. Please sign in again.");
  }

  const text = await res.text();
  const parsed = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const reason = typeof parsed?.reason === "string" ? parsed.reason : undefined;
    const error = typeof parsed?.error === "string" ? parsed.error : undefined;
    throw new ApiError(res.status, reason, messageFor(res.status, reason, error));
  }
  return parsed as T;
}

/**
 * Fetch a binary body (the installer, TRE-1713) with the bearer session attached,
 * mapping the same 401/403/error shapes as {@link request} but returning the raw
 * Blob instead of parsed JSON. The browser can't attach the bearer to a plain
 * `<a href>`, so the download must go through fetch and be saved from the Blob.
 */
async function requestBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["authorization"] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { method: "GET", headers, credentials: "include" });
  } catch {
    throw new ApiError(0, "network", "Couldn't reach the server. Check your connection.");
  }
  if (res.status === 401) {
    clearToken();
    onUnauthorized?.();
    throw new ApiError(401, "invalid_session", "Your session has expired. Please sign in again.");
  }
  if (!res.ok) {
    const parsed = safeJson(await res.text().catch(() => ""));
    const reason = typeof parsed?.reason === "string" ? parsed.reason : undefined;
    const error = typeof parsed?.error === "string" ? parsed.error : undefined;
    throw new ApiError(res.status, reason, messageFor(res.status, reason, error));
  }
  return res.blob();
}

function safeJson(text: string): Record<string, unknown> | undefined {
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

// --- auth ----------------------------------------------------------------

export const api = {
  authConfig: () => request<AuthConfig>("GET", "/auth/config", undefined, false),

  emailStart: (email: string) =>
    request<{ ok: true }>("POST", "/auth/email/start", { email }, false),

  emailVerify: (email: string, code: string, invite?: string) =>
    request<EmailVerifyResponse>("POST", "/auth/email/verify", { email, code, invite }, false),

  // --- account / roles (TRE-783) ---
  accountContext: () => request<AccountContext>("GET", "/account/context"),

  members: () => request<MembersResponse>("GET", "/account/members"),

  assignRole: (sub: string, role: Role) =>
    request<{ status: "assigned"; member: { sub: string; role: Role; from: Role } }>(
      "POST",
      `/account/members/${encodeURIComponent(sub)}/role`,
      { role },
    ),

  // --- pre-assign by email / invites (TRE-850) ---
  pending: () => request<PendingResponse>("GET", "/account/pending"),

  preAssign: (email: string, role: Role) =>
    request<{ status: "pending"; pending: PendingAssignment }>("POST", "/account/pending", {
      email,
      role,
    }),

  revokePending: (email: string) =>
    request<{ status: "revoked"; email: string }>(
      "DELETE",
      `/account/pending/${encodeURIComponent(email)}`,
    ),

  // --- invite links (TRE-1044) ---
  invites: () => request<InvitesResponse>("GET", "/account/invites"),

  createInvite: (role: Role, ttlHours?: number) =>
    request<CreateInviteResponse>("POST", "/account/invites", { role, ttlHours }),

  revokeInvite: (id: string) =>
    request<{ status: "revoked"; id: string }>(
      "DELETE",
      `/account/invites/${encodeURIComponent(id)}`,
    ),

  // --- entitlement (TRE-781) ---
  entitlement: () => request<EntitlementGate>("GET", "/entitlement"),

  // --- signed-installer distribution (TRE-1713, Option B) ---
  releaseCurrent: () => request<ReleaseInfo>("GET", "/releases/current"),

  downloadInstaller: () => requestBlob("/releases/download"),

  // --- admin governance (TRE-780) ---
  trash: () => request<TrashResponse>("GET", "/admin/trash"),
  locks: () => request<LocksResponse>("GET", "/admin/locks"),

  hardDelete: (skillId: string) =>
    request<{ status: "deleted" }>("POST", `/admin/skills/${encodeURIComponent(skillId)}/hard-delete`),

  restore: (skillId: string) =>
    request<{ status: "active" }>("POST", `/admin/skills/${encodeURIComponent(skillId)}/restore`),

  forceRelease: (skillId: string) =>
    request<{ released: boolean; priorHolder?: string }>(
      "POST",
      `/admin/skills/${encodeURIComponent(skillId)}/force-release`,
    ),

  // --- dev-handoff read surface (TRE-987) ---
  handoffs: (filters: HandoffFilters = {}) => {
    const q = new URLSearchParams();
    if (filters.triageStatus) q.set("triage_status", filters.triageStatus);
    if (filters.triggerSignalClass) q.set("trigger_signal_class", filters.triggerSignalClass);
    if (filters.clusterKey) q.set("cluster_key", filters.clusterKey);
    if (filters.limit != null) q.set("limit", String(filters.limit));
    if (filters.offset != null) q.set("offset", String(filters.offset));
    const qs = q.toString();
    return request<HandoffListResponse>("GET", `/admin/handoffs${qs ? `?${qs}` : ""}`);
  },

  handoff: (handoffId: string) =>
    request<{ handoff: HandoffDetail }>("GET", `/admin/handoffs/${encodeURIComponent(handoffId)}`),

  updateHandoffTriage: (
    handoffId: string,
    patch: { triage_status?: TriageStatus; backlog_issue_key?: string | null },
  ) =>
    request<{ handoff: HandoffSummary }>(
      "PATCH",
      `/admin/handoffs/${encodeURIComponent(handoffId)}`,
      patch,
    ),

  linkHandoffGap: (handoffId: string, gapId: string, backlogIssueKey: string | null) =>
    request<{ gap: HandoffGap }>(
      "PATCH",
      `/admin/handoffs/${encodeURIComponent(handoffId)}/gaps/${encodeURIComponent(gapId)}`,
      { backlog_issue_key: backlogIssueKey },
    ),
};
